import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as api from '../../api/educentialApi';

const ProfessionalDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.getDashboard();
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={fetchDashboardData}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { metrics, roleSpecificData, recentActivity, quickActions } = dashboardData || {};
  const professionalData = roleSpecificData || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome, {user?.email?.split('@')[0] || 'Professional'}!
          </h1>
          <p className="text-gray-600 mt-1">Advance your career and develop new skills</p>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <MetricCard
            label="Services Enrolled"
            value={metrics?.servicesEnrolled || 0}
            icon="💼"
            color="indigo"
          />
          <MetricCard
            label="Skills in Progress"
            value={metrics?.skillsInProgress || 0}
            icon="📚"
            color="green"
          />
          <MetricCard
            label="Network Connections"
            value={metrics?.networkingConnections || 0}
            icon="🤝"
            color="blue"
          />
          <MetricCard
            label="Notifications"
            value={metrics?.unreadNotifications || 0}
            icon="🔔"
            color="purple"
          />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickActions?.map((action) => (
              <Link
                key={action.id}
                to={action.actionUrl}
                className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200"
              >
                <div className="flex items-start">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{action.label}</h3>
                    <p className="text-sm text-gray-600 mt-1">{action.description}</p>
                  </div>
                  <span className="text-2xl ml-2">{action.icon}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Career Goals */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Career Goals</h2>
              <Link to="/services/career" className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">
                Manage Goals →
              </Link>
            </div>
            {professionalData.careerGoals && professionalData.careerGoals.length > 0 ? (
              <div className="space-y-4">
                {professionalData.careerGoals.slice(0, 3).map((goal) => (
                  <div key={goal.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium text-gray-900">{goal.title}</h3>
                      <span className="text-xs text-gray-500">
                        {new Date(goal.targetDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full"
                        style={{ width: `${goal.progress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{goal.progress}% complete</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No career goals set</p>
                <Link to="/services/career" className="text-indigo-600 hover:text-indigo-700 mt-2 inline-block">
                  Set your first goal
                </Link>
              </div>
            )}
          </div>

          {/* Skill Development */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Skill Development</h2>
              <Link to="/services/skills" className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">
                Browse Courses →
              </Link>
            </div>
            {professionalData.skillProgress && professionalData.skillProgress.length > 0 ? (
              <div className="space-y-4">
                {professionalData.skillProgress.slice(0, 5).map((skill, idx) => (
                  <div key={idx} className="border-l-4 border-indigo-500 pl-4">
                    <div className="flex justify-between items-center">
                      <p className="font-medium text-gray-900">{skill.skill}</p>
                      <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
                        {skill.level}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                      <div
                        className="bg-indigo-600 h-1.5 rounded-full"
                        style={{ width: `${skill.progress}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No skills in progress</p>
                <Link to="/services/skills" className="text-indigo-600 hover:text-indigo-700 mt-2 inline-block">
                  Start learning
                </Link>
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow p-6 lg:col-span-2">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
            {recentActivity && recentActivity.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recentActivity.slice(0, 6).map((activity) => (
                  <div key={activity.id} className="border border-gray-200 rounded-lg p-4">
                    <p className="font-medium text-gray-900">{activity.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-8 text-gray-500">No recent activity</p>
            )}
          </div>
        </div>

        {/* Opportunities */}
        {professionalData.opportunities && professionalData.opportunities.length > 0 && (
          <div className="mt-8 bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Opportunities for You</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {professionalData.opportunities.slice(0, 3).map((opp) => (
                <div key={opp.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <h3 className="font-medium text-gray-900">{opp.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{opp.organization}</p>
                  <span className="inline-block mt-2 text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
                    {opp.type}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ label, value, icon, color }) => {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-700',
    green: 'bg-green-50 text-green-700',
    blue: 'bg-blue-50 text-blue-700',
    purple: 'bg-purple-50 text-purple-700',
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`text-4xl ${colors[color]} p-3 rounded-lg`}>{icon}</div>
      </div>
    </div>
  );
};

export default ProfessionalDashboard;
